import moment from "moment-timezone";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { Body, Title } from "@vds/typography";
import { device } from "../../styles/breakpoints";
import "../../styles/deviceStyle.css";
import { MONTH_DATE_YYYY_WITH_COMA, HR_MIN_AM } from "../../global/constants/statistics.dateConstants";

/**
 * Responsive Dashboard Header
 *
 * Desktop: Large title + full date/time
 * Tablet: Medium title + abbreviated date/time
 * Mobile: Small title + compact date/time
 */

const HeaderContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 8px;

  /* Tablet: More spacing */
  @media ${device.tablet} {
    margin-bottom: 16px;
    gap: 12px;
  }

  /* Desktop: Full spacing */
  @media ${device.desktop} {
    margin-bottom: 16px;
    gap: 16px;
  }

  /* Mobile: Stack on very small screens */
  @media (max-width: 400px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`;

const TitleWrapper = styled.div`
  flex-shrink: 0;
`;

const DateTimeWrapper = styled.div`
  flex-shrink: 1;
  text-align: right;
  overflow: hidden;

  @media (max-width: 400px) {
    text-align: left;
    width: 100%;
  }
`;

const DashboardHeader = () => {
  const [date, setDate] = useState(new Date());
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Create timezone-adjusted moment object once and reuse it
  const timeZone = moment.tz.guess();
  const momentDate = moment(date).tz(timeZone);
  const formattedDate = momentDate.format(MONTH_DATE_YYYY_WITH_COMA);
  const formattedTime = momentDate.format(HR_MIN_AM);
  const timeZoneAbbr = momentDate.format("z");

  // Update time every second
  useEffect(() => {
    const timerID = setInterval(() => tick(), 1000);
    return () => {
      clearInterval(timerID);
    };
  }, []);

  const tick = () => {
    setDate(new Date());
  };

  // Responsive formatting
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  // Date/time formatting based on screen size
  const getFormattedDateTime = () => {
    if (isMobile) {
      // Mobile: Compact format (MM/DD, h:mm A)
      const shortDate = moment(date).format("MM/DD");
      const shortTime = moment(date).tz(timeZone).format("h:mm A");
      return `${shortDate}, ${shortTime}`;
    } else if (isTablet) {
      // Tablet: Medium format (MMM D, h:mm A z)
      const mediumDate = moment(date).format("MMM D,");
      const mediumTime = moment(date).tz(timeZone).format("h:mm A z");
      return `${mediumDate} ${mediumTime}`;
    } else {
      return `${formattedDate} ${formattedTime} ${timeZoneAbbr}`;
    }
  };

  // Title size based on screen size
  const getTitleSize = () => {
    if (isMobile) return "medium";
    if (isTablet) return "large";
    return "large";
  };

  // Body size based on screen size
  const getBodySize = () => {
    if (isMobile) return "medium";
    if (isTablet) return "large";
    return "large";
  };

  return (
    <HeaderContainer>
      <TitleWrapper>
        <Title
          size={getTitleSize()}
          color="#000000"
        >
          Dashboard
        </Title>
      </TitleWrapper>
      <DateTimeWrapper>
        <Body
          size={getBodySize()}
          color="#6F7171"
          tabIndex={100}
        >
          {getFormattedDateTime()}
        </Body>
      </DateTimeWrapper>
    </HeaderContainer>
  );
};

export default DashboardHeader;
